import type { DynamicSmartOfferEventPayload } from "@expert/websocket";

import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { useEffect, useEffectEvent } from "react";

import { DynamicSmartOffer } from "../DynamicSmartOffer";
import { useDynamicSmartOfferStore } from "../store/useDynamicSmartOfferStore";

const logger = getLogger({ module: "useDynamicSmartOfferListener" });

/**
 * Resolves the app's sessionId from the id on the incoming Kafka event.
 *
 * The Kafka event's `sessionId` field is actually the Amazon Connect **contactId**,
 * which may differ from the app's sessionId in callback and conference scenarios.
 * The caller provides this function to bridge that mismatch without coupling
 * sales-tools to the session store.
 */
export type ResolveSessionId = (contactId: string) => string;

const identity: ResolveSessionId = (id) => id;

export function useDynamicSmartOfferListener(resolveSessionId: ResolveSessionId = identity) {
    const resolve = useEffectEvent(resolveSessionId);

    useEffect(() => {
        return expertWorkspaceWebSocketEventBus.on(
            "ws_dynamic-smart-offer-event",
            (payload: DynamicSmartOfferEventPayload) => {
                // payload.sessionId is the contactId from the Kafka event — resolve it
                // to the app's sessionId so the store keys correctly in callback/conference calls.
                const sessionId = resolve(payload.sessionId);
                logger.info({ ...payload, resolvedSessionId: sessionId }, "Dynamic smart offer event received");
                useDynamicSmartOfferStore
                    .getState()
                    .receiveData(
                        sessionId,
                        new DynamicSmartOffer(payload.toneGuidance, payload.pitchPoints, payload.eventType),
                    );
            },
        );
    }, []);
}
